// The bindings table: every key the app answers to, as data.
//
// A table maps key sequences to commands (commands/command.ts), in
// sections. A registered scope names the sections that are live while it
// is (keys/router.ts); nothing outside this table says which key does
// what. `DEFAULT_BINDINGS` is the app's table; a remap is an `Overlay`
// laid over it (`overlay`), checked with `validate` before it is used.
//
// The grammar (docs/design.md, "Keyboard"): a motion or a selection is a
// prefix and an object letter, so one row (`] {object}`) binds the prefix
// for every object in `objects`. An object's capital letter means only
// those still waiting for the reviewer (`WAITING`), where that means
// something for the command.

import { commandFault, counted, OBJECTS, sameCommand, WAITING, type Command, type Dir, type Obj, type Only } from '../commands/command';
import { keyLabel, parseSpec, plain, type Chord } from './chord';

export const SECTIONS = ['field', 'guide', 'offer', 'files', 'unsent', 'send', 'review', 'dashboard', 'settings', 'grammar', 'app'] as const;
/** A named part of the table; a scope makes one or more live. */
export type Section = (typeof SECTIONS)[number];

/** How scopes rank: a text field's is innermost, a page's outermost (keys/router.ts). */
export type ScopeKind = 'page' | 'view' | 'popover' | 'modal' | 'field';

/**
 * The scopes components register, by name: each one's kind, and the
 * sections of the table live while it is registered (the one that wins a
 * shared key first). A component names its scope; which sections that
 * makes live is said here, with the table, and nowhere else.
 */
export const SCOPES = {
  /** Every page, under each page's own. */
  app: { kind: 'page', sections: ['app'] },
  /** Every view of a pull request. */
  pullRequest: { kind: 'page', sections: ['review', 'grammar'] },
  dashboard: { kind: 'page', sections: ['dashboard', 'grammar'] },
  /** The Settings page. */
  settings: { kind: 'page', sections: ['settings'] },
  /** The diff reader. */
  files: { kind: 'view', sections: ['files'] },
  /** The Review view: sending. */
  send: { kind: 'view', sections: ['send'] },
  /** The Review view's list of unsent drafts. */
  unsent: { kind: 'view', sections: ['unsent'] },
  /** The Find file offer over selected text. */
  offer: { kind: 'popover', sections: ['offer'] },
  /** The key guide, while it is open. */
  guide: { kind: 'modal', sections: ['guide'] },
  /** A comment box, while the focus is in it. */
  field: { kind: 'field', sections: ['field'] },
} as const satisfies Record<string, { kind: ScopeKind; sections: readonly Section[] }>;
export type ScopeName = keyof typeof SCOPES;

/**
 * The scopes registered together on each page, innermost first: what
 * `validate` and `shadows` read to tell which rows can hide which. A
 * popover or a text field's scope comes and goes over a page and is no
 * part of it; a modal holds the keys alone.
 */
export const PAGES: Readonly<Record<string, readonly ScopeName[]>> = {
  overview: ['pullRequest', 'app'],
  files: ['files', 'pullRequest', 'app'],
  snapshots: ['pullRequest', 'app'],
  review: ['send', 'unsent', 'pullRequest', 'app'],
  dashboard: ['dashboard', 'app'],
  settings: ['settings', 'app'],
  guide: ['guide'],
  elsewhere: ['app'],
};

/** The sections live on a page, innermost first. */
export const pageSections = (page: string): Section[] => (PAGES[page] ?? []).flatMap((scope) => [...SCOPES[scope].sections]);

/** A command an object letter completes. */
export type PerObject = { kind: 'move'; dir: Dir } | { kind: 'select' };

interface RowOptions {
  /** Also live while a text field has the focus (Mod+Enter to send it). Rows of a text field's own scope always are. */
  inText?: true;
  /** Left to a focused control that uses the key itself (Space or Enter on a button or a link). */
  yields?: true;
  /**
   * Another way to a command the grammar already reaches: `short` is kept
   * and listed beside the grammar's form; `deprecated` still works, is
   * left out of the guide's lists, and is named there as on its way out.
   */
  alias?: 'short' | 'deprecated';
}

/**
 * One row: a key spec (see `parseSpec`), and the command it names. A spec
 * ending in `{object}` takes an object letter there; a row of the same
 * section that spells one such sequence out (`] c`, `m o`) wins over it
 * for that sequence.
 */
export type Binding = ({ keys: string; command: Command } | { keys: string; perObject: PerObject }) & RowOptions;

export interface Bindings {
  /** The letter of each object the grammar names; its capital means only those waiting. */
  objects: Readonly<Record<string, Obj>>;
  sections: Readonly<Record<Section, readonly Binding[]>>;
}

const SLOT = '{object}';

const key = (keys: string, command: Command, o: RowOptions = {}): Binding => ({ keys, command, ...o });
/** A kept alias: listed beside the grammar's form. */
const short = (keys: string, command: Command, o: RowOptions = {}): Binding => ({ keys, command, alias: 'short', ...o });
/** A deprecated alias. */
const old = (keys: string, command: Command): Binding => ({ keys, command, alias: 'deprecated' });
const each = (keys: string, perObject: PerObject): Binding => ({ keys, perObject });

export const DEFAULT_BINDINGS: Bindings = {
  objects: { l: 'line', c: 'change', s: 'scope', f: 'file', d: 'draft', t: 'thread', p: 'pr' },
  sections: {
    // The grammar: live on every page that reviews or lists pull requests.
    grammar: [
      each('] {object}', { kind: 'move', dir: 1 }),
      each('[ {object}', { kind: 'move', dir: -1 }),
      short('n', { kind: 'move', object: 'change', dir: 1, only: 'unreviewed' }),
      short('p', { kind: 'move', object: 'change', dir: -1, only: 'unreviewed' }),
      old('N', { kind: 'move', object: 'change', dir: 1 }),
      old('P', { kind: 'move', object: 'change', dir: -1 }),

      key('v', { kind: 'range' }),
      each('m {object}', { kind: 'select' }),
      key('m o', { kind: 'grow', dir: 1 }),
      key('m i', { kind: 'grow', dir: -1 }),

      key('r', { kind: 'act', verb: 'review' }),
      key('R', { kind: 'act', verb: 'review', advance: true }),
      key('u', { kind: 'act', verb: 'unmark' }),
      key('c', { kind: 'act', verb: 'comment' }),
      key('e', { kind: 'act', verb: 'edit' }),
      key('y', { kind: 'act', verb: 'copy' }),
      key('x', { kind: 'act', verb: 'discard' }),

      key('g g', { kind: 'goto', place: 'top' }),
      key('g e', { kind: 'goto', place: 'end' }),
      old('G', { kind: 'goto', place: 'end' }),

      key('z a', { kind: 'fold', to: 'toggle' }),
      key('z o', { kind: 'fold', to: 'open' }),
      key('z c', { kind: 'fold', to: 'closed' }),
      key('z A', { kind: 'fold', to: 'toggle', level: true }),
      old('Z', { kind: 'fold', to: 'toggle', level: true }),
      key('z z', { kind: 'present', what: 'centre' }),
      key('z p', { kind: 'present', what: 'preview' }),
      key('z e', { kind: 'present', what: 'file-list' }),
      old('\\', { kind: 'present', what: 'file-list' }),
      key('z w', { kind: 'present', what: 'wrap' }),
      key('z l', { kind: 'present', what: 'layout' }),
      key('z d', { kind: 'present', what: 'algorithm' }),

      key(', f', { kind: 'open', menu: 'finder' }),
      key(', ?', { kind: 'open', menu: 'guide' }),
      short('?', { kind: 'open', menu: 'guide' }),
    ],
    // Every view of a pull request.
    review: [
      key('g o', { kind: 'goto', place: 'overview' }),
      key('g f', { kind: 'goto', place: 'files' }),
      key('g s', { kind: 'goto', place: 'commits' }),
      key('g r', { kind: 'goto', place: 'review' }),
      old('g d', { kind: 'goto', place: 'review' }),
      old('g p', { kind: 'goto', place: 'review' }),
      key('g F', { kind: 'goto', place: 'named-file' }),
      old('a', { kind: 'goto', place: 'review' }),
      key(', p', { kind: 'open', menu: 'stack' }),
    ],
    // The diff reader.
    files: [
      key('j', { kind: 'scroll', axis: 'y', dir: 1, by: 'line' }),
      key('k', { kind: 'scroll', axis: 'y', dir: -1, by: 'line' }),
      key('h', { kind: 'scroll', axis: 'x', dir: -1, by: 'line' }),
      key('l', { kind: 'scroll', axis: 'x', dir: 1, by: 'line' }),
      key('Ctrl+d', { kind: 'scroll', axis: 'y', dir: 1, by: 'half-page' }),
      key('Ctrl+u', { kind: 'scroll', axis: 'y', dir: -1, by: 'half-page' }),
      short('J', { kind: 'move', object: 'line', dir: 1 }),
      short('K', { kind: 'move', object: 'line', dir: -1 }),
      key('Space', { kind: 'fold', to: 'toggle', object: 'change' }, { yields: true }),
      short('/', { kind: 'open', menu: 'finder' }),
      key('Escape', { kind: 'cancel' }),
    ],
    // The Review view's list of unsent drafts: its verbs act only while the focus is in the list.
    unsent: [
      short('j', { kind: 'move', object: 'draft', dir: 1 }),
      short('k', { kind: 'move', object: 'draft', dir: -1 }),
      key('Space', { kind: 'act', verb: 'include', advance: true }, { yields: true }),
      key('a', { kind: 'act', verb: 'include', advance: true }),
      key('Enter', { kind: 'act', verb: 'open' }, { yields: true }),
    ],
    // The Review view: sending.
    send: [key('Mod+Enter', { kind: 'submit' }, { inText: true })],
    dashboard: [
      short('j', { kind: 'move', object: 'pr', dir: 1 }),
      short('k', { kind: 'move', object: 'pr', dir: -1 }),
      short('ArrowDown', { kind: 'move', object: 'pr', dir: 1 }),
      short('ArrowUp', { kind: 'move', object: 'pr', dir: -1 }),
      key('J', { kind: 'move', object: 'group', dir: 1 }),
      key('K', { kind: 'move', object: 'group', dir: -1 }),
      key('Enter', { kind: 'act', verb: 'open' }, { yields: true }),
      key('o', { kind: 'act', verb: 'open' }),
      short('h', { kind: 'fold', to: 'closed' }),
      short('l', { kind: 'fold', to: 'open' }),
      short('ArrowLeft', { kind: 'fold', to: 'closed' }),
      short('ArrowRight', { kind: 'fold', to: 'open' }),
      key(', r', { kind: 'refresh' }),
      short('r', { kind: 'refresh' }),
      short('/', { kind: 'open', menu: 'finder' }),
    ],
    // A comment box, while the focus is in it.
    field: [key('Mod+Enter', { kind: 'submit' }), key('Escape', { kind: 'cancel' })],
    // The key guide, while it is open.
    guide: [key('Escape', { kind: 'cancel' })],
    // The Find file offer over selected text.
    offer: [key('Escape', { kind: 'cancel' })],
    // The Settings page.
    settings: [key('Escape', { kind: 'cancel' })],
    // Every page.
    app: [key('F', { kind: 'open', menu: 'feedback' }), key(', ,', { kind: 'open', menu: 'settings' })],
  },
};

// ---------------------------------------------------------------- reading a table

/** What an object key names: the object, and `only` for its capital. */
export interface ObjectKey {
  object: Obj;
  only?: Only;
}

/** A row ready to match: one of the chord sequences its spec names. */
export interface Leaf {
  section: Section;
  /** The keys before the object letter, for a row that takes one; else the whole sequence. */
  seq: readonly Chord[];
  slot: boolean;
  row: Binding;
}

export interface Compiled {
  bindings: Bindings;
  leaves: ReadonlyMap<Section, readonly Leaf[]>;
  /** Every key that stands for an object: letters, and the capitals that mean something. */
  objectKeys: ReadonlyMap<string, ObjectKey>;
}

/** The chord sequences of a row's spec, and whether an object letter follows them. Throws as `parseSpec` does. */
export function parseKeys(keys: string): { seqs: Chord[][]; slot: boolean } {
  const slot = keys === SLOT || keys.endsWith(' ' + SLOT);
  const head = slot ? keys.slice(0, Math.max(0, keys.length - SLOT.length - 1)) : keys;
  if (head.includes('{')) throw new Error(`key spec "${keys}": ${SLOT} comes last`);
  if (slot && !head) throw new Error(`key spec "${keys}": an object letter follows a key`);
  const seqs = parseSpec(head);
  if (slot && seqs.some((s) => !s.every(plain))) throw new Error(`key spec "${keys}": a sequence is plain keys`);
  return { seqs, slot };
}

function objectKeysOf(objects: Readonly<Record<string, Obj>>): Map<string, ObjectKey> {
  const m = new Map<string, ObjectKey>();
  for (const [letter, object] of Object.entries(objects)) {
    m.set(letter, { object });
    const only = WAITING[object];
    if (only && letter.toUpperCase() !== letter) m.set(letter.toUpperCase(), { object, only });
  }
  return m;
}

const compiled = new WeakMap<Bindings, Compiled>();

/** `bindings` ready to match keys against; rows whose spec cannot be read are left out (`validate` names them). */
export function compile(bindings: Bindings): Compiled {
  const hit = compiled.get(bindings);
  if (hit) return hit;
  const leaves = new Map<Section, Leaf[]>();
  for (const section of SECTIONS) {
    const out: Leaf[] = [];
    for (const row of bindings.sections[section] ?? []) {
      let parsed: ReturnType<typeof parseKeys>;
      try {
        parsed = parseKeys(row.keys);
      } catch {
        continue;
      }
      for (const seq of parsed.seqs) out.push({ section, seq, slot: parsed.slot, row });
    }
    leaves.set(section, out);
  }
  const c: Compiled = { bindings, leaves, objectKeys: objectKeysOf(bindings.objects) };
  compiled.set(bindings, c);
  return c;
}

/** The command a row names once `objectKey` (for a row that takes one) is typed; null when that key completes nothing. */
export function commandOf(c: Compiled, leaf: Leaf, objectKey: string | null): Command | null {
  if ('command' in leaf.row) return objectKey === null ? leaf.row.command : null;
  const hit = objectKey === null ? undefined : c.objectKeys.get(objectKey);
  if (!hit) return null;
  const each = leaf.row.perObject;
  if (each.kind === 'select') return hit.only ? null : { kind: 'select', object: hit.object };
  return hit.only ? { kind: 'move', object: hit.object, dir: each.dir, only: hit.only } : { kind: 'move', object: hit.object, dir: each.dir };
}

const sameChord = (a: Chord, b: Chord) => a.key === b.key && a.ctrl === b.ctrl && a.meta === b.meta;
const startsWith = (seq: readonly Chord[], head: readonly Chord[]) => head.length <= seq.length && head.every((c, i) => sameChord(c, seq[i]));

/** A row a typed sequence completes, with its command. */
export interface Hit {
  leaf: Leaf;
  command: Command;
}

/** The rows of `section` that `typed` completes exactly: a row that spells the sequence out, else the rows that take its last key as an object letter. */
export function hits(c: Compiled, section: Section, typed: readonly Chord[]): Hit[] {
  const spelled: Hit[] = [];
  const composed: Hit[] = [];
  for (const leaf of c.leaves.get(section) ?? []) {
    if (leaf.slot) {
      const last = typed[typed.length - 1];
      const command = typed.length === leaf.seq.length + 1 && startsWith(typed, leaf.seq) && plain(last) ? commandOf(c, leaf, last.key) : null;
      if (command) composed.push({ leaf, command });
    } else if (typed.length === leaf.seq.length && startsWith(typed, leaf.seq)) spelled.push({ leaf, command: commandOf(c, leaf, null)! });
  }
  return spelled.length ? spelled : composed;
}

/** Whether a row of `section` spells out the sequence `seq` then `key` (so the rows that take `key` as an object letter there do not). */
const spelledOut = (c: Compiled, section: Section, seq: readonly Chord[], key: string) =>
  (c.leaves.get(section) ?? []).some((l) => !l.slot && l.seq.length === seq.length + 1 && startsWith(l.seq, seq) && plain(l.seq[seq.length]) && l.seq[seq.length].key === key);

/** A key that may follow what was typed, and the command it would complete (null when it only leads on to more keys). */
export interface Continuation {
  key: string;
  command: Command | null;
  /** The key is an object letter. */
  object: boolean;
  alias?: Binding['alias'];
}

/** The keys that may follow `typed` in `sections`, the innermost section's first; a key is listed once. */
export function continuations(c: Compiled, sections: readonly Section[], typed: readonly Chord[]): Continuation[] {
  const out = new Map<string, Continuation>();
  const add = (x: Continuation) => {
    const had = out.get(x.key);
    // A key that leads on is replaced by what it completes, never the reverse.
    if (!had || (had.command === null && x.command !== null)) out.set(x.key, x);
  };
  for (const section of sections)
    for (const leaf of c.leaves.get(section) ?? []) {
      if (!startsWith(leaf.seq, typed)) continue;
      const rest = leaf.seq.length - typed.length;
      if (rest === 0 && leaf.slot) {
        for (const [key, hit] of c.objectKeys) if (!hit.only && !spelledOut(c, section, leaf.seq, key)) add({ key, command: commandOf(c, leaf, key), object: true });
      } else if (rest === 1 && !leaf.slot) add({ key: leaf.seq[typed.length].key, command: commandOf(c, leaf, null), object: false, alias: leaf.row.alias });
      else if (rest >= 1) add({ key: leaf.seq[typed.length].key, command: null, object: false });
    }
  return [...out.values()];
}

/** Whether more keys after `typed` can complete a row of `section`. */
export function leadsOn(c: Compiled, section: Section, typed: readonly Chord[]): boolean {
  return (c.leaves.get(section) ?? []).some((leaf) => startsWith(leaf.seq, typed) && (leaf.seq.length > typed.length || (leaf.slot && leaf.seq.length === typed.length)));
}

/** Whether any row of `section` names a command a count multiplies. */
export function takesCounts(c: Compiled, section: Section): boolean {
  return (c.leaves.get(section) ?? []).some((leaf) => counted('perObject' in leaf.row ? leaf.row.perObject.kind : leaf.row.command.kind));
}

// ---------------------------------------------------------------- writing keys

const chordLabel = (c: Chord) => `${c.meta ? '⌘ ' : ''}${c.ctrl ? 'Ctrl ' : ''}${keyLabel(c.key)}`;

/** How a sequence is written for a person (`]c`, `⌘ Enter`). */
export const sequenceLabel = (seq: readonly Chord[]) => seq.map(chordLabel).join('');

/**
 * The keys that reach `command` in `sections` (every section by default),
 * as written for a person: the grammar's form first, then kept aliases;
 * deprecated aliases only with `deprecated`. Counts and named objects
 * (`at`) are no part of a binding and are ignored.
 */
export function keysFor(bindings: Bindings, command: Command, sections: readonly Section[] = SECTIONS, deprecated = false): string[] {
  const c = compile(bindings);
  const bare = { ...command } as Record<string, unknown>;
  delete bare.count;
  delete bare.at;
  const want = bare as Command;
  const found: { label: string; rank: number }[] = [];
  for (const section of sections)
    for (const leaf of c.leaves.get(section) ?? []) {
      const rank = leaf.row.alias === 'deprecated' ? 2 : leaf.row.alias === 'short' ? 1 : 0;
      if (rank === 2 && !deprecated) continue;
      if (!leaf.slot) {
        if ('command' in leaf.row && sameCommand(leaf.row.command, want)) found.push({ label: sequenceLabel(leaf.seq), rank });
        continue;
      }
      for (const [key] of c.objectKeys) {
        if (spelledOut(c, section, leaf.seq, key)) continue;
        const made = commandOf(c, leaf, key);
        if (made && sameCommand(made, want)) found.push({ label: sequenceLabel(leaf.seq) + key, rank });
      }
    }
  const labels = found.sort((a, b) => a.rank - b.rank).map((f) => f.label);
  return [...new Set(labels)];
}

// ---------------------------------------------------------------- remapping

/** A row of an overlay: a binding, or `command: null` to unbind the keys. */
export type OverlayRow = Binding | { keys: string; command: null };

/** Changes to lay over a table: object letters to set (or, with null, drop), and rows per section. */
export interface Overlay {
  objects?: Readonly<Record<string, Obj | null>>;
  sections?: Partial<Record<Section, readonly OverlayRow[]>>;
}

const sequencesOf = (keys: string): string[] => {
  try {
    const { seqs, slot } = parseKeys(keys);
    return seqs.map((s) => sequenceLabel(s) + (slot ? ' ' + SLOT : ''));
  } catch {
    return [keys];
  }
};

/**
 * `base` with `over` laid on it. In a section, an overlay row takes the
 * place of every base row that names one of its key sequences (so binding
 * a key rebinds it), and comes after the rows kept; a row with a null
 * command only removes. The result is not checked: see `validate`.
 */
export function overlay(base: Bindings, over: Overlay): Bindings {
  const objects: Record<string, Obj> = { ...base.objects };
  for (const [letter, object] of Object.entries(over.objects ?? {})) {
    if (object === null) delete objects[letter];
    else objects[letter] = object;
  }
  const sections = { ...base.sections } as Record<string, readonly Binding[]>;
  for (const [section, rows] of Object.entries(over.sections ?? {})) {
    const taken = new Set((rows ?? []).flatMap((r) => sequencesOf(r.keys)));
    const kept = (base.sections[section as Section] ?? []).filter((r) => !sequencesOf(r.keys).some((s) => taken.has(s)));
    const added = (rows ?? []).filter((r): r is Binding => !('command' in r && r.command === null));
    sections[section] = [...kept, ...added];
  }
  return { objects, sections: sections as Bindings['sections'] };
}

// ---------------------------------------------------------------- checking a table

/** Where a row is: its section and its key spec. */
export interface Where {
  section: string;
  keys: string;
}

/** What is wrong with a table. */
export type BindingError =
  | { kind: 'unknown-section'; section: string }
  /** The key spec cannot be read. */
  | ({ kind: 'bad-keys'; why: string } & Where)
  /** The row names no command the app has. */
  | ({ kind: 'unknown-command'; why: string } & Where)
  /** An object letter that is not one lower-case letter, or names no object. */
  | { kind: 'bad-object'; letter: string; why: string }
  /** Two rows of a section name the same key sequence. */
  | ({ kind: 'duplicate'; sequence: string } & Where)
  /** `leaf` is a whole binding and also the start of `prefix`: the longer one could never be typed. */
  | { kind: 'conflict'; leaf: Where; prefix: Where; sequence: string }
  /** The sequence can never be typed: it starts with a digit (a count), or passes through Escape (which cancels a sequence). */
  | ({ kind: 'unreachable'; why: 'starts-with-a-count' | 'passes-escape' } & Where)
  /** The row is live where text is typed (a text field's own section, or `inText`) and its keys would be typed as text: only a Ctrl or Meta chord, or one key that types nothing (Escape, Enter, a function key), may be bound there. */
  | ({ kind: 'typed-as-text' } & Where)
  /** A row option that is not one. */
  | ({ kind: 'bad-option'; why: string } & Where)
  /** No page can reach the row: on every page its section is live on, `by` (a row of a section inside it) names the same keys first. */
  | ({ kind: 'shadowed'; sequence: string; by: Where } & Where);

/** A row hidden on one page by an inner section's row for the same keys (see `shadows`). */
export interface Shadow {
  page: string;
  row: Where;
  by: Where;
  sequence: string;
}

/** The sections whose rows are live while text is typed whatever the row says. */
const TEXT_SECTIONS: ReadonlySet<string> = new Set(Object.values(SCOPES).filter((s) => s.kind === 'field').flatMap((s) => [...s.sections]));

/** Whether a chord pressed in a text field types into it, or edits it. */
const typesText = (c: Chord) => plain(c) && (c.key.length === 1 || ['Backspace', 'Delete', 'Tab'].includes(c.key) || c.key.startsWith('Arrow'));

const OPTIONS: Record<string, (v: unknown) => string | null> = {
  inText: (v) => (v === true || v === undefined ? null : 'inText is true or absent'),
  yields: (v) => (v === true || v === undefined ? null : 'yields is true or absent'),
  alias: (v) => (v === 'short' || v === 'deprecated' || v === undefined ? null : 'alias is short or deprecated'),
};

/** Every concrete sequence of the table that can be read, object letters filled in, with its row. A spelled-out row stands in for the composed one of its section. */
function sequences(bindings: Bindings): { where: Where; keys: string[]; label: string; composed: boolean }[] {
  const objectKeys = objectKeysOf(Object.fromEntries(Object.entries(bindings.objects ?? {}).filter(([, o]) => (OBJECTS as readonly unknown[]).includes(o))));
  const out: { where: Where; keys: string[]; label: string; composed: boolean }[] = [];
  for (const [section, rows] of Object.entries(bindings.sections ?? {})) {
    if (!(SECTIONS as readonly string[]).includes(section)) continue;
    const mine: typeof out = [];
    for (const row of rows ?? []) {
      let parsed: ReturnType<typeof parseKeys>;
      try {
        parsed = parseKeys(row.keys);
      } catch {
        continue;
      }
      if ('perObject' in row !== parsed.slot) continue;
      for (const seq of parsed.seqs) {
        const tails = parsed.slot ? [...objectKeys].filter(([, hit]) => !('perObject' in row && row.perObject.kind === 'select' && hit.only)).map(([k]) => [k]) : [[]];
        for (const tail of tails) {
          const keys = [...seq.map(chordLabel), ...tail];
          mine.push({ where: { section, keys: row.keys }, keys, label: keys.join(''), composed: parsed.slot });
        }
      }
    }
    const spelled = new Set(mine.filter((x) => !x.composed).map((x) => x.keys.join(' ')));
    out.push(...mine.filter((x) => !x.composed || !spelled.has(x.keys.join(' '))));
  }
  return out;
}

/**
 * Every row an inner section's row for the same keys hides, page by page
 * (`PAGES`). Hiding is how a view gives a key its own meaning (the draft
 * list's `a`), so it is no error by itself: `validate` refuses only a row
 * hidden on every page its section is live on. A remap shows these before
 * it is used.
 */
export function shadows(bindings: Bindings): Shadow[] {
  const all = sequences(bindings);
  const out: Shadow[] = [];
  for (const page of Object.keys(PAGES)) {
    const live = pageSections(page);
    const seen = new Map<string, Where>();
    for (const section of live)
      for (const x of all) {
        if (x.where.section !== section) continue;
        const id = x.keys.join(' ');
        const by = seen.get(id);
        if (by && by.section !== section) out.push({ page, row: x.where, by, sequence: x.label });
        else if (!by) seen.set(id, x.where);
      }
  }
  return out;
}

const perObjectFault = (v: unknown): string | null => {
  if (typeof v !== 'object' || v === null) return 'an object row names a move or a selection';
  const p = v as Record<string, unknown>;
  if (p.kind === 'select') return Object.keys(p).length === 1 ? null : 'select takes nothing else';
  if (p.kind === 'move') return p.dir === 1 || p.dir === -1 ? null : 'move needs dir 1 or -1';
  return `no object row of kind ${JSON.stringify(p.kind)}`;
};

/**
 * Everything wrong with `bindings`, in table order; empty when it can be
 * used. A sequence that is both a binding and the start of another is a
 * conflict wherever the two rows are, so a prefix means one family on
 * every page. Rows that are hidden by an inner section on some pages only
 * are no error (see `shadows`).
 */
export function validate(bindings: Bindings): BindingError[] {
  const errors: BindingError[] = [];
  for (const [letter, object] of Object.entries(bindings.objects ?? {})) {
    if (!/^[a-z]$/.test(letter)) errors.push({ kind: 'bad-object', letter, why: 'an object letter is one lower-case letter' });
    else if (!(OBJECTS as readonly unknown[]).includes(object)) errors.push({ kind: 'bad-object', letter, why: `no object ${JSON.stringify(object)}` });
  }
  for (const [section, rows] of Object.entries(bindings.sections ?? {})) {
    if (!(SECTIONS as readonly string[]).includes(section)) {
      errors.push({ kind: 'unknown-section', section });
      continue;
    }
    const seen = new Set<string>();
    for (const row of rows ?? []) {
      const where: Where = { section, keys: row.keys };
      let parsed: ReturnType<typeof parseKeys>;
      try {
        parsed = parseKeys(row.keys);
      } catch (e) {
        errors.push({ kind: 'bad-keys', ...where, why: e instanceof Error ? e.message : String(e) });
        continue;
      }
      const fault = 'perObject' in row ? perObjectFault(row.perObject) : commandFault((row as { command: unknown }).command);
      if (fault) errors.push({ kind: 'unknown-command', ...where, why: fault });
      for (const [name, value] of Object.entries(row)) {
        if (name === 'keys' || name === 'command' || name === 'perObject') continue;
        const why = Object.hasOwn(OPTIONS, name) ? OPTIONS[name](value) : `no option ${name}`;
        if (why) errors.push({ kind: 'bad-option', ...where, why });
      }
      if ('perObject' in row !== parsed.slot) {
        errors.push({ kind: 'bad-keys', ...where, why: parsed.slot ? `${SLOT} needs an object row (perObject)` : `an object row's keys end in ${SLOT}` });
        continue;
      }
      // Where text is typed, a row may be one chord that types nothing; anything else would act on what the person writes.
      if ((TEXT_SECTIONS.has(section) || row.inText === true) && (parsed.slot || parsed.seqs.some((seq) => seq.length !== 1 || typesText(seq[0])))) errors.push({ kind: 'typed-as-text', ...where });
      for (const seq of parsed.seqs) {
        if (/^[0-9]$/.test(seq[0].key) && plain(seq[0])) errors.push({ kind: 'unreachable', ...where, why: 'starts-with-a-count' });
        if (seq.slice(1).some((c) => c.key === 'Escape')) errors.push({ kind: 'unreachable', ...where, why: 'passes-escape' });
        // Two rows of a kind for one sequence: two spelled out, or two that take an object letter after the same keys.
        const id = `${parsed.slot ? 'composed' : 'spelled'} ${seq.map(chordLabel).join(' ')}`;
        if (seen.has(id)) errors.push({ kind: 'duplicate', ...where, sequence: seq.map(chordLabel).join('') });
        seen.add(id);
      }
    }
  }
  const all = sequences(bindings);
  const reported = new Set<string>();
  for (const a of all)
    for (const b of all) {
      if (a.keys.length >= b.keys.length || !a.keys.every((k, i) => k === b.keys[i])) continue;
      const id = `${a.where.section}\n${a.where.keys}\n${b.where.section}\n${b.where.keys}`;
      if (reported.has(id)) continue;
      reported.add(id);
      errors.push({ kind: 'conflict', leaf: a.where, prefix: b.where, sequence: a.label });
    }
  // A row hidden on every page its section is live on can never run.
  const hidden = shadows(bindings);
  const told = new Set<string>();
  for (const h of hidden) {
    const id = `${h.row.section}\n${h.row.keys}\n${h.sequence}`;
    if (told.has(id)) continue;
    told.add(id);
    const pages = Object.keys(PAGES).filter((page) => pageSections(page).includes(h.row.section as Section));
    const on = new Set(hidden.filter((x) => x.row.section === h.row.section && x.row.keys === h.row.keys && x.sequence === h.sequence).map((x) => x.page));
    if (pages.every((page) => on.has(page))) errors.push({ kind: 'shadowed', ...h.row, sequence: h.sequence, by: h.by });
  }
  return errors;
}
